import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import { WorkflowConfiguration } from "skedyul-core/features/workflows/client";
import { buildEmptyWorkflowListStats } from "skedyul-core/features/workflows/features/workflowRuns/resources/types";
// Minimal realtime workflow run event payload used for optimistic UI updates
type WorkflowRunRealtime = {
  workflowRun: {
    id: string;
    completedAt?: string | null;
    cancelledAt?: string | null;
    steps?: Array<{
      id: string;
      status?: string;
      scheduledAt?: string | null;
      startedAt?: string | null;
      completedAt?: string | null;
    }>;
  } | null;
};

type CreateWorkflow =
  inferRouterInputs<AppRouter>["workflows"]["createWorkflow"];

export type UpdateWorkflow =
  inferRouterInputs<AppRouter>["workflows"]["updateWorkflow"];

export type ValidateWorkflow =
  inferRouterOutputs<AppRouter>["workflows"]["validateWorkflow"];

export type ListWorkflow =
  inferRouterOutputs<AppRouter>["workflows"]["getWorkflows"]["records"][number];

export type GetWorkflow =
  inferRouterOutputs<AppRouter>["workflows"]["getWorkflowVersion"];

export type RunWorkflow =
  inferRouterOutputs<AppRouter>["workflows"]["runWorkflow"];

export type GetWorkflowRun =
  inferRouterOutputs<AppRouter>["workflows"]["getWorkflowRun"];

export type TriggerList =
  inferRouterOutputs<AppRouter>["workflows"]["getTriggers"]["records"][number];

export type GetTrigger =
  inferRouterOutputs<AppRouter>["workflows"]["getTrigger"];

export type GetWorkflowVersion =
  inferRouterOutputs<AppRouter>["workflows"]["getWorkflowVersions"]["records"][number];

export type GetWorkflowVersionMeta =
  inferRouterOutputs<AppRouter>["workflows"]["getWorkflowVersions"]["meta"];

export type WorkflowRunListItem =
  inferRouterOutputs<AppRouter>["workflows"]["getWorkflowRuns"]["records"][number];

// Event listener type
export type EventListener = {
  id: string;
  triggerId: string;
  type: string;
  condition: string;
  inputs: Record<string, unknown>;
};

// Action type
export type Action = {
  id: string;
  triggerId: string;
  label: string;
  icon?: string | null;
  modelId: string;
  batch: boolean;
  inputs: Record<string, unknown>;
  identifierKey?: string | null;
};

// Event subscription type for triggers
export type TriggerEventSubscription = {
  id: string;
  eventType: string;
  condition: string | null;
  description: string | null;
  isEnabled: boolean;
  inputMappings?: Record<string, string> | null;
  matchConfig?: unknown | null;
  agentId: string | null;
  triggerId: string | null;
  createdAt: string;
  updatedAt: string;
};

// Trigger type with proper structure
export type Trigger = {
  id: string;
  workflowId: string;
  workflowVersionId: string | null;
  followLatest?: boolean;
  appInstallationId?: string | null;
  handle: string;
  workflow: {
    id: string;
    name: string;
  };
  workflowVersion: {
    id: string;
    versionLabel: string;
  };
  eventListeners: EventListener[];
  webhooks: unknown[];
  agents: unknown[];
  actions: Action[];
  subflows: unknown[];
  events: unknown[];
  eventSubscriptions?: TriggerEventSubscription[];
  hooks?: Array<{
    id: string;
    hookEvent: string;
    setup?: unknown[] | null;
    inputMapping?: Record<string, string> | null;
    isEnabled: boolean;
  }>;
  formEndpoint?: {
    allowedDomains: string[];
    published: boolean;
    path: string;
  };
  envVariables?: Array<{
    id: string;
    key: string;
    label: string;
    value: string;
    visibility: "ENCRYPTED" | "VISIBLE";
    editable: boolean;
    lastFour?: string | null;
  }>;
};

export interface workflowState {
  workflow: {
    isLoading: boolean;
    isSaving: boolean;
    data: (GetWorkflow & { trigger?: Trigger | null }) | null;
  };
  workflows: {
    isLoading: boolean;
    data: ListWorkflow[];
  };
  triggers: {
    isLoading: boolean;
    data: TriggerList[];
  };
  // used to search all versions across the system
  versions: {
    isLoading: boolean;
    data: GetWorkflowVersion[];
    query: {
      q: string;
      limit: number;
      page: number;
    };
  };
  workflowRun: {
    isLoading: boolean;

    /**
     * one time test inputs
     */
    inputs: Record<string, string>;

    /**
     * File upload state per input key
     */
    fileUploads: Record<
      string,
      {
        isLoading: boolean;
        file?: {
          id: string;
          name: string;
        };
      }
    >;

    data: GetWorkflowRun | null;
    error: string | null;
  };
  workflowRuns: {
    isLoading: boolean;
    data: WorkflowRunListItem[];
  };
}

const initialState: workflowState = {
  workflow: {
    isLoading: false,
    isSaving: false,
    data: null,
  },
  workflows: {
    isLoading: true,
    data: [],
  },
  triggers: {
    isLoading: false,
    data: [],
  },
  versions: {
    isLoading: false,
    data: [],
    query: {
      q: "",
      limit: 10,
      page: 0,
    },
  },
  workflowRun: {
    isLoading: false,
    inputs: {},
    fileUploads: {},
    data: null,
    error: null,
  },
  workflowRuns: {
    isLoading: false,
    data: [],
  },
};

export const getWorkflowVersions = createAsyncThunk(
  "workflows/getWorkflowVersions",
  async (payload: {
    q: string;
    limit: number;
    page: number;
    workflowId?: string;
  }) => {
    return await trpc.workflows.getWorkflowVersions.query(payload);
  },
);

export const getWorkflowWithVersion = createAsyncThunk(
  "workflows/getWorkflow",
  async (payload: { id: string; versionId: string }) => {
    return await trpc.workflows.getWorkflowVersion.query(payload);
  },
);

export const LATEST_VERSION_VALUE = "__latest__";

export const updateTriggerVersion = createAsyncThunk(
  "workflows/updateTriggerVersion",
  async (payload: {
    id: string;
    /** Concrete version id, null to deactivate, or "__latest__" to auto-follow tip */
    workflowVersionId: string | null;
  }) => {
    return await trpc.workflows.updateTriggerVersion.mutate(payload);
  },
);

export const getWorkflows = createAsyncThunk(
  "workflows/getWorkflows",
  async () => {
    return await trpc.workflows.getWorkflows.query();
  },
);

export const validateWorkflow = createAsyncThunk(
  "workflows/validate",
  async (payload: CreateWorkflow) => {
    return await trpc.workflows.validateWorkflow.mutate(payload);
  },
);

export const createWorkflow = createAsyncThunk(
  "workflows/create",
  async (payload: CreateWorkflow) => {
    return await trpc.workflows.createWorkflow.mutate(payload);
  },
);

export const updateWorkflow = createAsyncThunk(
  "workflows/update",
  async (payload: UpdateWorkflow) => {
    return await trpc.workflows.updateWorkflow.mutate(payload);
  },
);

export const saveConfiguration = createAsyncThunk(
  "workflows/saveConfiguration",
  async (payload: {
    workflowId: string;
    versionId: string;
    configuration: WorkflowConfiguration;
  }) => {
    return await trpc.workflows.saveConfiguration.mutate(payload);
  },
);

export const runWorkflow = createAsyncThunk(
  "workflows/run",
  async (payload: {
    id: string;
    versionId: string;
    inputs: Record<string, unknown>;
  }) => {
    return await trpc.workflows.runWorkflow.mutate(payload);
  },
);

export const getWorkflowRun = createAsyncThunk(
  "workflows/getWorkflowRun",
  async (payload: { workflowRunId: string }) => {
    return await trpc.workflows.getWorkflowRun.query(payload);
  },
);

export const cancelWorkflowRun = createAsyncThunk(
  "workflows/cancelWorkflowRun",
  async (payload: { workflowRunId: string }) => {
    return await trpc.workflows.cancelWorkflowRun.mutate(payload);
  },
);

export const getWorkflowRuns = createAsyncThunk(
  "workflows/getWorkflowRuns",
  async (payload: { workflowId: string; limit?: number }) => {
    return await trpc.workflows.getWorkflowRuns.query(payload);
  },
);

export const getTriggers = createAsyncThunk(
  "workflows/getTriggers",
  async () => {
    return await trpc.workflows.getTriggers.query();
  },
);

export const upsertTriggerEnvVariablesAsync = createAsyncThunk(
  "workflows/upsertTriggerEnvVariables",
  async (payload: {
    triggerId: string;
    variables: Array<{
      key: string;
      label: string;
      description: string | null;
      value: string;
      visibility: "ENCRYPTED" | "VISIBLE";
      editable: boolean;
    }>;
  }) => {
    return await trpc.workflows.upsertTriggerEnvVariables.mutate(payload);
  },
);

export const createTriggerEventListenerAsync = createAsyncThunk(
  "workflows/createTriggerEventListener",
  async (payload: {
    triggerId: string;
    condition: string;
    inputs: Record<string, string>;
    type: string;
  }) => {
    const response = await trpc.workflows.createEventListener.mutate({
      triggerId: payload.triggerId,
      type: payload.type,
      condition: payload.condition,
      inputs: payload.inputs,
    });

    return response;
  },
);

export const updateTriggerEventListenerAsync = createAsyncThunk(
  "workflows/updateTriggerEventListener",
  async (payload: {
    triggerId: string;
    condition: string;
    inputs: Record<string, unknown>;
    type: string;
    id: string;
  }) => {
    const response = await trpc.workflows.updateEventListener.mutate({
      id: payload.id,
      triggerId: payload.triggerId,
      type: payload.type,
      condition: payload.condition,
      inputs: payload.inputs,
    });

    return response;
  },
);

export const createTriggerActionAsync = createAsyncThunk(
  "workflows/createTriggerAction",
  async (payload: {
    triggerId: string;
    name: string;
    handle: string;
    description: string;
    icon: string;
    customResourceModelId: string;
    individual?: boolean;
    batch?: boolean;
    chat?: boolean;
    inputsMap?: Record<string, unknown>;
  }) => {
    const response = await trpc.workflows.createAction.mutate({
      triggerId: payload.triggerId,
      label: payload.name,
      icon: payload.icon,
      modelId: payload.customResourceModelId,
      batch: payload.batch,
      inputs: payload.inputsMap || {},
    });

    return response;
  },
);

export const updateTriggerActionAsync = createAsyncThunk(
  "workflows/updateTriggerAction",
  async (payload: {
    id: string;
    triggerId: string;
    label: string;
    icon?: string;
    modelId: string;
    batch: boolean;
    inputs: Record<string, unknown>;
  }) => {
    const response = await trpc.workflows.updateAction.mutate({
      id: payload.id,
      triggerId: payload.triggerId,
      label: payload.label,
      icon: payload.icon,
      modelId: payload.modelId,
      batch: payload.batch,
      inputs: payload.inputs || {},
    });

    return response;
  },
);

export const deleteTriggerActionAsync = createAsyncThunk(
  "workflows/deleteTriggerAction",
  async (payload: { id: string }) => {
    const response = await trpc.workflows.deleteAction.mutate({
      id: payload.id,
    });
    return { id: payload.id, ...response };
  },
);

// Event subscriptions for triggers
export const getTriggerEventSubscriptions = createAsyncThunk(
  "workflows/getTriggerEventSubscriptions",
  async (payload: { triggerId: string }) => {
    const subscriptions = await trpc.eventSubscriptions.list.query({
      triggerId: payload.triggerId,
    });
    return {
      triggerId: payload.triggerId,
      subscriptions: subscriptions.map((s) => ({
        id: s.id,
        eventType: s.eventType,
        condition: s.condition,
        description: s.description,
        isEnabled: s.isEnabled,
        inputMappings: s.inputMappings as Record<string, string> | null,
        agentId: s.agentId,
        triggerId: s.triggerId,
        createdAt: String(s.createdAt),
        updatedAt: String(s.updatedAt),
      })),
    };
  },
);

export const createTriggerEventSubscription = createAsyncThunk(
  "workflows/createTriggerEventSubscription",
  async (payload: {
    triggerId: string;
    eventType: string;
    condition?: string;
    description?: string;
    isEnabled?: boolean;
  }) => {
    const subscription = await trpc.eventSubscriptions.create.mutate(payload);
    return {
      id: subscription.id,
      eventType: subscription.eventType,
      condition: subscription.condition,
      description: subscription.description,
      isEnabled: subscription.isEnabled,
      agentId: subscription.agentId,
      triggerId: subscription.triggerId,
      createdAt: String(subscription.createdAt),
      updatedAt: String(subscription.updatedAt),
    };
  },
);

export const updateTriggerEventSubscription = createAsyncThunk(
  "workflows/updateTriggerEventSubscription",
  async (payload: {
    id: string;
    eventType?: string;
    condition?: string | null;
    description?: string | null;
    isEnabled?: boolean;
  }) => {
    const subscription = await trpc.eventSubscriptions.update.mutate(payload);
    return {
      id: subscription.id,
      eventType: subscription.eventType,
      condition: subscription.condition,
      description: subscription.description,
      isEnabled: subscription.isEnabled,
      agentId: subscription.agentId,
      triggerId: subscription.triggerId,
      createdAt: String(subscription.createdAt),
      updatedAt: String(subscription.updatedAt),
    };
  },
);

export const deleteTriggerEventSubscription = createAsyncThunk(
  "workflows/deleteTriggerEventSubscription",
  async (payload: { id: string }) => {
    await trpc.eventSubscriptions.delete.mutate(payload);
    return payload.id;
  },
);

export const toggleTriggerEventSubscription = createAsyncThunk(
  "workflows/toggleTriggerEventSubscription",
  async (payload: { id: string }) => {
    const subscription =
      await trpc.eventSubscriptions.toggleEnabled.mutate(payload);
    return {
      id: subscription.id,
      eventType: subscription.eventType,
      condition: subscription.condition,
      description: subscription.description,
      isEnabled: subscription.isEnabled,
      agentId: subscription.agentId,
      triggerId: subscription.triggerId,
      createdAt: String(subscription.createdAt),
      updatedAt: String(subscription.updatedAt),
    };
  },
);

export const getNewTrigger = createAsyncThunk("workflows/getNewTrigger", () => {
  return {
    workflowId: "",
    workflowVersionId: "",
    handle: "",
    id: "new",
    workflow: {
      id: "",
      name: "",
    },
    workflowVersion: {
      id: "",
      versionLabel: "",
    },
    eventListeners: [],
    webhooks: [],
    agents: [],
    actions: [],
    subflows: [],
    events: [],
  };
});

export const getTrigger = createAsyncThunk(
  "workflows/getTrigger",
  async (payload: { id: string }) => {
    if (payload.id === "new") {
      return null;
    }

    return await trpc.workflows.getTrigger.query(payload);
  },
);

export const workflowSlice = createSlice({
  name: "workflows",
  initialState,
  reducers: {
    updateConfiguration: (
      state,
      action: PayloadAction<WorkflowConfiguration>,
    ) => {
      if (!state.workflow.data) return;

      state.workflow.data.configuration = {
        ...state.workflow.data.configuration,
        ...action.payload,
        steps: {
          ...state.workflow.data.configuration.steps,
          ...action.payload.steps,
        },
      };
    },
    /**
     * Seed focused workflow from the list payload when detail+S3 load fails
     * (common for app-provided workflows). Enough for Configuration UI.
     */
    seedWorkflowFromList: (state, action: PayloadAction<ListWorkflow>) => {
      const listWorkflow = action.payload;
      const previous = state.workflow.data;
      // List payload lacks schema/configuration; keep prior detail fields when present.
      state.workflow.data = {
        ...(previous as unknown as Record<string, unknown>),
        ...listWorkflow,
        schema: previous?.schema,
        configuration: previous?.configuration,
      } as unknown as NonNullable<typeof state.workflow.data>;
    },
    updateInputs: (
      state,
      action: PayloadAction<{
        workflowRunId: string;
        key: string;
        value: string;
      }>,
    ) => {
      state.workflowRun.inputs = {
        ...state.workflowRun.inputs,
        [action.payload.key]: action.payload.value,
      };
    },
    setFileUploadLoading: (
      state,
      action: PayloadAction<{
        key: string;
        isLoading: boolean;
      }>,
    ) => {
      if (!state.workflowRun.fileUploads[action.payload.key]) {
        state.workflowRun.fileUploads[action.payload.key] = {
          isLoading: false,
        };
      }
      const uploadState = state.workflowRun.fileUploads[action.payload.key];
      if (uploadState) {
        uploadState.isLoading = action.payload.isLoading;
      }
    },
    setFileUploaded: (
      state,
      action: PayloadAction<{
        key: string;
        file: {
          id: string;
          name: string;
        };
      }>,
    ) => {
      state.workflowRun.fileUploads[action.payload.key] = {
        isLoading: false,
        file: action.payload.file,
      };
    },
    applyWorkflowRunRealtimePatch: (
      state,
      action: PayloadAction<WorkflowRunRealtime>,
    ) => {
      const current = state.workflowRun.data;
      const update = action.payload.workflowRun;
      if (!current || !update || current.id !== update.id) return;

      if (Object.prototype.hasOwnProperty.call(update, "completedAt")) {
        current.completedAt = update.completedAt ?? null;
      }
      if (Object.prototype.hasOwnProperty.call(update, "cancelledAt")) {
        current.cancelledAt = update.cancelledAt ?? null;
      }

      if (Array.isArray(update.steps) && Array.isArray(current.steps)) {
        for (const s of update.steps) {
          const target = current.steps.find((st) => st.id === s.id);
          if (!target) continue;
          if (typeof s.status === "string") target.status = s.status;
          if (Object.prototype.hasOwnProperty.call(s, "scheduledAt")) {
            target.scheduledAt = s.scheduledAt ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(s, "startedAt")) {
            target.startedAt = s.startedAt ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(s, "completedAt")) {
            target.completedAt = s.completedAt ?? null;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTriggerEventListenerAsync.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(createTriggerEventListenerAsync.fulfilled, (state, action) => {
        state.workflow.isSaving = false;
        const created = action.payload as EventListener;
        const eventListeners = state.workflow.data?.trigger?.eventListeners;
        if (eventListeners) {
          eventListeners.push(created);
        } else if (state.workflow.data?.trigger) {
          state.workflow.data.trigger.eventListeners = [created];
        }
      })
      .addCase(createTriggerEventListenerAsync.rejected, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(updateTriggerEventListenerAsync.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(updateTriggerEventListenerAsync.fulfilled, (state, action) => {
        state.workflow.isSaving = false;
        const updated = action.payload as EventListener;
        const eventListeners = state.workflow.data?.trigger?.eventListeners;
        if (eventListeners && updated?.id) {
          const index = eventListeners.findIndex(
            (e: EventListener) => e.id === updated.id,
          );
          if (index !== -1) {
            eventListeners[index] = { ...eventListeners[index], ...updated };
          }
        }
      })
      .addCase(updateTriggerEventListenerAsync.rejected, (state) => {
        state.workflow.isSaving = false;
      })

      // actions
      .addCase(createTriggerActionAsync.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(createTriggerActionAsync.fulfilled, (state, action) => {
        state.workflow.isSaving = false;
        const created = action.payload as Action;
        const actions = state.workflow.data?.trigger?.actions;
        if (actions) {
          actions.push(created);
        } else if (state.workflow.data?.trigger) {
          state.workflow.data.trigger.actions = [created];
        }
      })
      .addCase(createTriggerActionAsync.rejected, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(updateTriggerActionAsync.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(updateTriggerActionAsync.fulfilled, (state, action) => {
        state.workflow.isSaving = false;
        const updated = action.payload as Action;
        const actions = state.workflow.data?.trigger?.actions;
        if (actions && updated?.id) {
          const index = actions.findIndex((a: Action) => a.id === updated.id);
          if (index !== -1) {
            actions[index] = { ...actions[index], ...updated };
          }
        }
      })
      .addCase(updateTriggerActionAsync.rejected, (state) => {
        state.workflow.isSaving = false;
      })

      // delete action
      .addCase(deleteTriggerActionAsync.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(deleteTriggerActionAsync.fulfilled, (state, action) => {
        state.workflow.isSaving = false;
        const actions = state.workflow.data?.trigger?.actions;
        const deletedId = action.payload?.id;
        if (actions && deletedId) {
          const index = actions.findIndex((a: Action) => a.id === deletedId);
          if (index !== -1) {
            actions.splice(index, 1);
          }
        }
      })
      .addCase(deleteTriggerActionAsync.rejected, (state) => {
        state.workflow.isSaving = false;
      })

      // getTriggers list
      .addCase(getTriggers.pending, (state) => {
        state.triggers.isLoading = true;
      })
      .addCase(getTriggers.fulfilled, (state, action) => {
        state.triggers.isLoading = false;
        state.triggers.data = action.payload.records;
      })
      .addCase(getTriggers.rejected, (state) => {
        state.triggers.isLoading = false;
      })

      // get single trigger into workflow.data.trigger when available
      .addCase(getTrigger.pending, (state) => {
        state.workflow.isLoading = true;
      })
      .addCase(getTrigger.fulfilled, (state, action) => {
        state.workflow.isLoading = false;
        if (state.workflow.data && action.payload) {
          // only set if workflow is loaded in state
          // Cast to unknown first, then to Trigger with defaults
          const payload = action.payload as unknown as Partial<Trigger>;
          state.workflow.data.trigger = {
            ...payload,
            webhooks: payload.webhooks ?? [],
            agents: payload.agents ?? [],
            subflows: payload.subflows ?? [],
            eventListeners: payload.eventListeners ?? [],
          } as Trigger;
        }
      })
      .addCase(getTrigger.rejected, (state) => {
        state.workflow.isLoading = false;
      })

      // initialize new trigger placeholder
      .addCase(getNewTrigger.fulfilled, (state, action) => {
        if (state.workflow.data) {
          state.workflow.data.trigger = action.payload as Trigger;
        }
      })

      .addCase(updateTriggerVersion.pending, (state, action) => {
        state.workflow.isSaving = true;
        // Update both the focused trigger and the triggers list so UI reflects immediately
        const argVersionId = action.meta.arg.workflowVersionId;
        const targetId = action.meta.arg.id;
        const followLatest = argVersionId === LATEST_VERSION_VALUE;

        const resolveTipId = (versions: Array<{ id: string }>) =>
          versions[0]?.id ?? null;

        const applyOptimistic = (
          trigger: {
            workflowVersionId?: string | null;
            followLatest?: boolean;
            workflowVersion?: unknown;
          },
          versions: Array<{ id: string; versionLabel: string | null }>,
        ) => {
          if (followLatest) {
            const tipId = resolveTipId(versions);
            trigger.followLatest = true;
            trigger.workflowVersionId = tipId;
            const matched = tipId
              ? versions.find((v) => v.id === tipId)
              : null;
            trigger.workflowVersion = matched
              ? { id: matched.id, versionLabel: matched.versionLabel }
              : null;
            return;
          }

          trigger.followLatest = false;
          trigger.workflowVersionId = argVersionId ?? null;
          const matched = argVersionId
            ? versions.find((v) => v.id === argVersionId)
            : null;
          trigger.workflowVersion = matched
            ? { id: matched.id, versionLabel: matched.versionLabel }
            : null;
        };

        const focusedVersions = state.workflow.data?.versions ?? [];

        if (
          state.workflow.data?.trigger &&
          state.workflow.data.trigger.id === targetId
        ) {
          applyOptimistic(state.workflow.data.trigger, focusedVersions);
        }

        // Also update the workflow's triggers array if present (when UI is using fallbackTrigger)
        const workflowWithTriggers = state.workflow.data as unknown as {
          triggers?: Array<Partial<Trigger>>;
        } | null;
        if (
          workflowWithTriggers?.triggers &&
          Array.isArray(workflowWithTriggers.triggers)
        ) {
          const t = workflowWithTriggers.triggers.find(
            (tr) => tr?.id === targetId,
          );
          if (t) {
            applyOptimistic(t, focusedVersions);
          }
        }

        // Update the global triggers list so other views stay in sync
        if (
          Array.isArray(state.triggers.data) &&
          state.triggers.data.length > 0
        ) {
          const idx = state.triggers.data.findIndex((tr) => tr.id === targetId);
          if (idx !== -1) {
            const current = state.triggers.data[idx];
            const patched = { ...current } as (typeof state.triggers.data)[number] & {
              followLatest?: boolean;
            };
            applyOptimistic(patched, focusedVersions);
            state.triggers.data[idx] = patched;
          }
        }

        // Patch the workflows list (spreadsheet) so Version + active sort update immediately
        if (Array.isArray(state.workflows.data) && state.workflows.data.length > 0) {
          for (const workflow of state.workflows.data) {
            const trigger = workflow.triggers?.find((tr) => tr.id === targetId);
            if (!trigger) continue;
            applyOptimistic(
              trigger as typeof trigger & { followLatest?: boolean },
              workflow.versions,
            );
            break;
          }
        }
      })
      .addCase(updateTriggerVersion.fulfilled, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(updateTriggerVersion.rejected, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(getWorkflowWithVersion.pending, (state) => {
        state.workflow.isLoading = true;
        state.workflowRun.data = null;
      })
      .addCase(getWorkflowWithVersion.fulfilled, (state, action) => {
        state.workflow.isLoading = false;
        state.workflow.data = action.payload;
      })
      .addCase(getWorkflowWithVersion.rejected, (state) => {
        state.workflow.isLoading = false;
      })
      .addCase(getWorkflows.pending, (state) => {
        state.workflows.isLoading = true;
      })
      .addCase(getWorkflows.fulfilled, (state, action) => {
        state.workflows.isLoading = false;
        state.workflows.data = action.payload.records;
      })
      .addCase(getWorkflows.rejected, (state) => {
        state.workflows.isLoading = false;
      })
      .addCase(validateWorkflow.pending, (state) => {
        state.workflow.isLoading = true;
      })
      .addCase(validateWorkflow.fulfilled, (state) => {
        state.workflow.isLoading = false;
      })
      .addCase(validateWorkflow.rejected, (state) => {
        state.workflow.isLoading = false;
      })
      .addCase(createWorkflow.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(createWorkflow.fulfilled, (state, action) => {
        state.workflow.isSaving = false;

        /**
         * insert the new workflow into the list
         */
        if (!action.payload.workflow || !action.payload.version) {
          // error handling
          return;
        }

        state.workflows.data = [
          ...state.workflows.data,
          {
            app: null,
            ...action.payload.workflow,
            versions: [
              {
                id: action.payload.version.id,
                version: action.payload.version.version,
                versionLabel: action.payload.version.versionLabel,
                createdAt: action.payload.version.createdAt,
                workflowId: action.payload.version.workflowId,
              },
            ],
            triggers: [],
            stats: buildEmptyWorkflowListStats(),
          } as ListWorkflow,
        ];
      })
      .addCase(createWorkflow.rejected, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(saveConfiguration.pending, (state) => {
        state.workflow.isSaving = true;
      })
      .addCase(saveConfiguration.fulfilled, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(saveConfiguration.rejected, (state) => {
        state.workflow.isSaving = false;
      })
      .addCase(runWorkflow.pending, (state) => {
        state.workflowRun.isLoading = true;
        state.workflowRun.error = null;
        state.workflowRun.data = null;
      })
      .addCase(runWorkflow.fulfilled, (state, action) => {
        state.workflowRun.isLoading = false;
        state.workflowRun.data = action.payload;
      })
      .addCase(runWorkflow.rejected, (state, action) => {
        state.workflowRun.isLoading = false;
        state.workflowRun.error =
          action.error.message || "Failed to run workflow";
        state.workflowRun.data = null;
      })
      .addCase(getWorkflowRun.pending, (state) => {
        state.workflowRun.isLoading = true;
      })
      .addCase(getWorkflowRun.fulfilled, (state, action) => {
        state.workflowRun.isLoading = false;
        state.workflowRun.data = action.payload;
      })
      .addCase(getWorkflowRun.rejected, (state) => {
        state.workflowRun.isLoading = false;
      })
      .addCase(cancelWorkflowRun.pending, (state) => {
        state.workflowRun.isLoading = true;
      })
      .addCase(cancelWorkflowRun.fulfilled, (state) => {
        state.workflowRun.isLoading = false;
      })
      .addCase(cancelWorkflowRun.rejected, (state) => {
        state.workflowRun.isLoading = false;
      })
      .addCase(getWorkflowVersions.pending, (state) => {
        state.versions.isLoading = true;
      })
      .addCase(getWorkflowVersions.fulfilled, (state, action) => {
        state.versions.isLoading = false;
        state.versions.data = action.payload.records;
      })
      .addCase(getWorkflowVersions.rejected, (state) => {
        state.versions.isLoading = false;
      })

      .addCase(getWorkflowRuns.pending, (state) => {
        state.workflowRuns.isLoading = true;
      })
      .addCase(getWorkflowRuns.fulfilled, (state, action) => {
        state.workflowRuns.isLoading = false;
        state.workflowRuns.data = action.payload.records;
      })
      .addCase(getWorkflowRuns.rejected, (state) => {
        state.workflowRuns.isLoading = false;
      })

      .addCase(upsertTriggerEnvVariablesAsync.pending, (state, action) => {
        const variable = state.workflow.data?.trigger?.envVariables?.find(
          (v) => v.key === action.meta.arg.variables[0]?.key,
        );

        if (variable) {
          variable.lastFour =
            action.meta.arg.variables[0]?.value?.slice(-4) ?? "";
        }
      })

      // Event subscriptions for triggers
      .addCase(getTriggerEventSubscriptions.fulfilled, (state, action) => {
        if (state.workflow.data?.trigger) {
          state.workflow.data.trigger.eventSubscriptions =
            action.payload.subscriptions;
        }
      })
      .addCase(createTriggerEventSubscription.fulfilled, (state, action) => {
        const subscriptions =
          state.workflow.data?.trigger?.eventSubscriptions ?? [];
        if (state.workflow.data?.trigger) {
          state.workflow.data.trigger.eventSubscriptions = [
            action.payload,
            ...subscriptions,
          ];
        }
      })
      .addCase(updateTriggerEventSubscription.fulfilled, (state, action) => {
        const subscriptions = state.workflow.data?.trigger?.eventSubscriptions;
        if (subscriptions) {
          const index = subscriptions.findIndex(
            (s) => s.id === action.payload.id,
          );
          if (index !== -1) {
            subscriptions[index] = action.payload;
          }
        }
      })
      .addCase(deleteTriggerEventSubscription.fulfilled, (state, action) => {
        if (state.workflow.data?.trigger?.eventSubscriptions) {
          state.workflow.data.trigger.eventSubscriptions =
            state.workflow.data.trigger.eventSubscriptions.filter(
              (s) => s.id !== action.payload,
            );
        }
      })
      .addCase(toggleTriggerEventSubscription.fulfilled, (state, action) => {
        const subscriptions = state.workflow.data?.trigger?.eventSubscriptions;
        if (subscriptions) {
          const index = subscriptions.findIndex(
            (s) => s.id === action.payload.id,
          );
          if (index !== -1) {
            subscriptions[index] = action.payload;
          }
        }
      });
  },
});
// Action creators are generated for each case reducer function
export const {
  updateConfiguration,
  updateInputs,
  seedWorkflowFromList,
  applyWorkflowRunRealtimePatch,
  setFileUploadLoading,
  setFileUploaded,
} = workflowSlice.actions;

export default workflowSlice.reducer;
